import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { User, ArrowRight, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * PUBLIC PROFILE PREVIEW PAGE — LOCKED
 * 
 * Route: /u/{handle}
 * 
 * Shows ONLY:
 * - Placard categories
 * - Minimal context text
 * - "Join to connect" CTA
 * 
 * ❌ No auto-follow
 * ❌ No auto-request
 * ❌ No data exposure beyond placard
 */

export default function PublicProfile() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await axios.get(`${API}/api/profile/public/${handle}`);
        setProfile(response.data);
      } catch (error) {
        if (error.response?.status === 404) {
          setNotFound(true);
        }
        console.error('Failed to load profile:', error);
      } finally {
        setLoading(false);
      }
    };

    if (handle) {
      loadProfile();
    }
  }, [handle]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" 
             style={{ borderColor: 'var(--fy-text-secondary)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <User className="w-16 h-16 mb-4" style={{ color: 'var(--fy-text-disabled)' }} />
        <h1 className="text-xl font-bold mb-2" style={{ color: 'var(--fy-text-primary)' }}>
          Profile not found
        </h1>
        <p className="text-sm mb-6" style={{ color: 'var(--fy-text-secondary)' }}>
          This profile doesn&apos;t exist or is not publicly visible.
        </p>
        <Button
          onClick={() => navigate('/auth')}
          className="rounded-full"
          style={{ backgroundColor: 'var(--fy-accent-positive)', color: '#FFFFFF' }}
        >
          Join LikeKind
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header 
        className="sticky top-0 z-40 p-4"
        style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}
      >
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_kindplatform/artifacts/my73uiau_file_00000000f09c720c9463ab506718d330.png" 
              alt="LikeKind" 
              className="w-8 h-8 rounded-lg" 
            />
            <span className="font-bold" style={{ color: 'var(--fy-text-primary)' }}>LikeKind</span>
          </Link>
          <Button
            onClick={() => navigate('/auth')}
            size="sm"
            className="rounded-full"
            style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-primary)' }}
          >
            Sign In
          </Button>
        </div>
      </header>

      {/* Profile Content */}
      <main className="max-w-lg mx-auto px-4 py-12">
        {/* Avatar */}
        <div className="flex justify-center mb-6">
          <div 
            className="w-24 h-24 rounded-full flex items-center justify-center overflow-hidden"
            style={{ backgroundColor: 'var(--fy-surface-muted)' }}
          >
            {profile.profile_photo ? (
              <img 
                src={profile.profile_photo}
                alt={profile.name}
                className="w-24 h-24 object-cover"
              />
            ) : (
              <User className="w-12 h-12" style={{ color: 'var(--fy-text-secondary)' }} />
            )}
          </div>
        </div>

        {/* Name & Handle */}
        <div className="text-center mb-8">
          <h1 
            className="text-2xl font-bold mb-1"
            style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}
          >
            {profile.name || profile.username}
          </h1>
          <p style={{ color: 'var(--fy-text-secondary)' }}>
            @{profile.username}
          </p>
        </div>

        {/* Placard Categories (if public) */}
        {profile.primary_categories && profile.primary_categories.length > 0 && (
          <div className="mb-8">
            <h2 
              className="text-sm font-medium text-center mb-4"
              style={{ color: 'var(--fy-text-disabled)' }}
            >
              INTERESTS
            </h2>
            <div className="flex flex-wrap justify-center gap-2">
              {profile.primary_categories.map((category, i) => {
                const catId = typeof category === 'string' ? category : (category.category_id || category.id || '');
                const catName = category.name || catId.charAt(0).toUpperCase() + catId.slice(1);
                return (
                <span
                  key={i}
                  className="px-4 py-2 rounded-full text-sm"
                  style={{ 
                    backgroundColor: 'var(--fy-surface-muted)',
                    color: 'var(--fy-text-primary)'
                  }}
                >
                  {catName}
                </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Welcome Text */}
        <div 
          className="text-center p-6 rounded-2xl mb-8"
          style={{ backgroundColor: 'var(--fy-surface-muted)' }}
        >
          <p style={{ color: 'var(--fy-text-secondary)' }}>
            {profile.name || profile.username} is on LikeKind — a calmer way to connect with people who share your interests.
          </p>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            onClick={() => navigate('/membership')}
            className="rounded-full px-8 h-12 font-bold"
            style={{ backgroundColor: 'var(--fy-accent-positive)', color: '#FFFFFF' }}
            data-testid="join-to-connect-btn"
          >
            Join to Connect
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          
          <p 
            className="text-xs mt-4"
            style={{ color: 'var(--fy-text-disabled)' }}
          >
            No ads. No tracking. No algorithms.
          </p>
        </div>

        {/* Trust Badge */}
        <div className="flex justify-center mt-12">
          <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--fy-text-disabled)' }}>
            <Shield className="w-4 h-4" />
            <span>Privacy-first platform</span>
          </div>
        </div>
      </main>
    </div>
  );
}
